import { Controller, useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";
import { useCallback, useEffect } from "react";
import Button from "../../../../../ui/Button/Button";
import TableInputGroup from "../../../../../ui/TableInputGroup/TableInputGroup";
import TableInput from "../../../../../ui/TableInputGroup/TableInput";
import styles from "./AddKeywords.module.scss";
import { zodResolver } from "@hookform/resolvers/zod";

const schema = z.object({
  keywords: z
    .object({ keyword: z.string().nonempty("Required") })
    .array()
    .min(1, { message: "Required" }),
});

type AddKeywordsFormData = z.infer<typeof schema>;

interface AddKeywordsProps {
  onBack: () => void;
  onSubmit: (formData: AddKeywordsFormData) => void;
  defaultValues?: AddKeywordsFormData;
}

function AddKeywords({ onBack, onSubmit, defaultValues }: AddKeywordsProps) {
  const { trigger, control, handleSubmit, setFocus, setValue } =
    useForm<AddKeywordsFormData>({
      resolver: zodResolver(schema),
      defaultValues: defaultValues ?? { keywords: [{ keyword: "" }] },
    });

  const { fields, append, remove, insert } = useFieldArray({
    control,
    name: "keywords",
  });

  const submit = useCallback(
    async (formData: AddKeywordsFormData) => {
      if (!(await trigger("keywords"))) {
        return;
      }

      onSubmit(formData);
    },
    [onSubmit, trigger],
  );

  // FIXME: Need to create an inner component to handle this so it happens when the input is mounted
  useEffect(() => {
    setTimeout(() => {
      setFocus("keywords.0.keyword");
    }, 1);
  }, [setFocus]);

  return (
    <form className={styles.keywordsForm} onSubmit={handleSubmit(submit)}>
      <TableInputGroup
        header={
          <>
            <span>Keywords</span>
            <span className={styles.keywordsCount}>{fields.length}</span>
          </>
        }
      >
        {fields.map((field, index) => (
          <Controller
            control={control}
            key={field.id}
            name={`keywords.${index}.keyword`}
            render={({
              field: { value, onChange, ref },
              fieldState: { error },
            }) => (
              <TableInput
                error={error?.message}
                addValue={(o) => {
                  if (o?.index) {
                    insert(
                      o.index,
                      { keyword: o?.value ?? "" },
                      { shouldFocus: true },
                    );
                  } else {
                    append({ keyword: o?.value ?? "" }, { shouldFocus: true });
                  }
                }}
                addValues={(newValues) => {
                  const valuesToAppend = newValues;

                  if (value === "") {
                    setValue(
                      `keywords.${index}.keyword`,
                      valuesToAppend.shift() ?? "",
                    );
                  }

                  if (valuesToAppend.length > 0) {
                    append(
                      valuesToAppend.map((newValue) => ({
                        keyword: newValue,
                      })),
                      { shouldFocus: true },
                    );
                  }
                }}
                index={index}
                key={field.id}
                onChange={onChange}
                ref={ref}
                removeValue={() => {
                  remove(index);
                }}
                setFocus={(newIndex) => {
                  setFocus(`keywords.${newIndex}.keyword`);
                }}
                totalCount={fields.length}
                value={value}
              />
            )}
          />
        ))}
      </TableInputGroup>
      <div className={styles.footerButtons}>
        <Button onClick={onBack} variant="outline">
          Back
        </Button>
        <Button type="submit" variant="primary">
          Continue
        </Button>
      </div>
    </form>
  );
}

export default AddKeywords;
